import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Breadcrumb } from '@/components/ui/breadcrumb';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { LoadingOverlay } from '@/components/ui/loading-overlay';
import { useAuthenticatedFetch } from '@/hooks/useAuthenticatedFetch';
import { formatTimestamp, formatRelativeTime } from '@/lib/formatters';
import type { AdminUserPaymentsResponse } from '@/types/admin';

const EVENT_CODE_VARIANTS: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  subscription_created: 'default',
  subscription_renewed: 'default',
  pack_purchased: 'default',
  subscription_cancelled: 'destructive',
  payment_failed: 'destructive',
  payment_refunded: 'destructive',
  subscription_updated: 'secondary',
};

export default function UserPaymentsPage() {
  const { userId } = useParams<{ userId: string }>();
  const authenticatedFetch = useAuthenticatedFetch();
  const [data, setData] = useState<AdminUserPaymentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!userId) return;
    const controller = new AbortController();

    async function fetch() {
      setLoading(true);
      setError(null);
      try {
        const res = await authenticatedFetch(`users/${userId}/payments`, {
          signal: controller.signal,
        });
        if (!res.ok) throw new Error(`Failed to load payments (${res.status})`);
        setData((await res.json()) as AdminUserPaymentsResponse);
      } catch (e) {
        if (!controller.signal.aborted) {
          setError(e instanceof Error ? e.message : 'Failed to load payments');
        }
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }

    fetch();
    return () => controller.abort();
  }, [authenticatedFetch, userId]);

  return (
    <>
      <LoadingOverlay visible={loading} message="Loading payments..." />
      <div className="space-y-4 sm:space-y-6">
        <Breadcrumb
          items={[
            { label: 'Dashboard', to: '/' },
            { label: 'Users', to: '/users' },
            { label: userId ?? 'User', to: `/users/${userId}` },
            { label: 'Payments' },
          ]}
        />
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Payment history</h1>
            <p className="text-muted-foreground font-mono text-xs mt-1">{userId}</p>
          </div>
          <Button variant="outline" size="sm" asChild>
            <Link to={`/users/${userId}/cost-audit`}>Cost Audit →</Link>
          </Button>
        </div>

        {error && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {data && (
          <div className="grid gap-4 sm:grid-cols-2">
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total credits added</CardDescription>
                <CardTitle className="text-2xl font-mono">
                  {data.totalCreditsAdded.toLocaleString()}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardDescription>Total amount paid</CardDescription>
                <CardTitle className="text-2xl font-mono">
                  ${(data.totalAmountPaid / 100).toFixed(2)}
                </CardTitle>
              </CardHeader>
            </Card>
          </div>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Payment events (payments-table)</CardTitle>
            <CardDescription>
              {data
                ? `${data.payments.length} events · newest first`
                : 'Stripe payment events'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {data && data.payments.length > 0 ? (
              <div className="overflow-hidden rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Subscription</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Amount paid</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.payments.map((payment, idx) => (
                      <TableRow key={`${payment.created}-${idx}`}>
                        <TableCell className="text-xs font-mono">
                          <span title={formatTimestamp(payment.created)}>
                            {formatRelativeTime(payment.created)}
                          </span>
                          <p className="text-muted-foreground">{formatTimestamp(payment.created)}</p>
                        </TableCell>
                        <TableCell>
                          <Badge variant={EVENT_CODE_VARIANTS[payment.eventCode] ?? 'outline'}>
                            {payment.eventCode.replace(/_/g, ' ')}
                          </Badge>
                        </TableCell>
                        <TableCell>{payment.subscriptionName ?? '—'}</TableCell>
                        <TableCell className="text-right font-mono">
                          {payment.credits != null ? payment.credits.toLocaleString() : '—'}
                        </TableCell>
                        <TableCell className="text-right font-mono">
                          {payment.amountPaid != null
                            ? `$${(payment.amountPaid / 100).toFixed(2)}`
                            : '—'}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {payment.description ?? '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                  <TableFooter>
                    <TableRow>
                      <TableCell colSpan={3}>Totals</TableCell>
                      <TableCell className="text-right font-mono">{data.totalCreditsAdded.toLocaleString()}</TableCell>
                      <TableCell className="text-right font-mono">${(data.totalAmountPaid / 100).toFixed(2)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableFooter>
                </Table>
              </div>
            ) : (
              !loading && <p className="text-sm text-muted-foreground">No payment events found.</p>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
